import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../i18n';
import { Language, LoaderType } from '@shared/enum';
import { UserRole } from './userApi';
import { RootState } from './configureStore';

export interface PageState {
    fullname?: string;
    language: Language;
    userRole?: UserRole;
    isLoading: boolean;
    hideLoader: boolean;
    activeLoaders: LoaderType[];
    isAuthenticated: boolean | null;
    isTournamentRoute: boolean;
    isErrorRoute: boolean;
    breadcrumb: {
        courseDetailTitle?: string | null;
    } | null;
}
const initialState: PageState = {
    fullname: undefined,
    language: Language.EN,
    activeLoaders: [],
    isLoading: false,
    hideLoader: false,
    isAuthenticated: null,
    isTournamentRoute: false,
    isErrorRoute: false,
    breadcrumb: null,
};

export const pageSlice = createSlice({
    name: 'pageSlice',
    initialState,
    reducers: {
        setIsAuthenticated: (state, actions: PayloadAction<boolean>) => {
            state.isAuthenticated = actions.payload;
            if (!state.isAuthenticated) {
                state.fullname = undefined;
            }
        },
        setBaseInfo: (state, actions: PayloadAction<{ fullname: string; language: Language; userRole?: UserRole }>) => {
            state.fullname = actions.payload.fullname;
            state.language = actions.payload.language;
            state.userRole = actions.payload.userRole;
            i18n.changeLanguage(state.language.toLowerCase());
        },
        setFullName: (state, actions: PayloadAction<string>) => {
            state.fullname = actions.payload;
        },
        setLanguage: (state, actions: PayloadAction<Language>) => {
            state.language = actions.payload;
            i18n.changeLanguage(state.language.toLowerCase());
        },
        enableLoading: (state, actions: PayloadAction<LoaderType>) => {
            if (!state.activeLoaders.some((item) => item === actions.payload)) {
                state.activeLoaders.push(actions.payload);
            }
            if (actions.payload === LoaderType.selectSimulator) {
                state.hideLoader = true;
            }
            state.isLoading = true;
        },
        disableLoading: (state, actions: PayloadAction<LoaderType>) => {
            state.activeLoaders = state.activeLoaders.filter((item) => item !== actions.payload);

            state.isLoading = state.activeLoaders.length > 0;
        },
        setIsTournamentRoute: (state, actions: PayloadAction<boolean>) => {
            state.isTournamentRoute = actions.payload;
        },
        setIsErrorRoute: (state, actions: PayloadAction<boolean>) => {
            state.isErrorRoute = actions.payload;
        },
        setCourseDetailTitle: (state, actions: PayloadAction<string>) => {
            state.breadcrumb = {
                ...state.breadcrumb,
                courseDetailTitle: actions.payload,
            };
        },
        clearCourseDetailTitle: (state) => {
            state.breadcrumb = {
                ...state.breadcrumb,
                courseDetailTitle: null,
            };
        },
    },
});

export const selectState = (state: RootState) => state.pageSlice;
export const selectLanguage = createSelector(selectState, (state) => state.language);
export const selectIsLoading = createSelector(selectState, (state) => state.isLoading);
export const selectHideLoader = createSelector(selectState, (state) => state.hideLoader);
export const selectFullname = createSelector(selectState, (state) => state.fullname);
export const selectBaseInfo = createSelector(selectState, (state) => {
    return {
        fullname: state.fullname,
        language: state.language,
        userRole: state.userRole,
    };
});
export const selectUserRole = createSelector(selectState, (state) => state.userRole);
export const selectIsTournamentRoute = createSelector(selectState, (state) => state.isTournamentRoute);
export const selectIsAuthenticated = createSelector(selectState, (state) => state.isAuthenticated);
export const selectIsError = createSelector(selectState, (state) => state.isErrorRoute);
export const selectCourseDetailTitle = createSelector(selectState, (state) =>
    state.breadcrumb ? (state.breadcrumb.courseDetailTitle ?? 'N/A') : 'N/A',
);

export const {
    setIsTournamentRoute,
    setIsErrorRoute,
    setLanguage,
    enableLoading,
    disableLoading,
    setBaseInfo,
    setFullName,
    setIsAuthenticated,
    setCourseDetailTitle,
    clearCourseDetailTitle,
} = pageSlice.actions;
